// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/CaseDetail/components/NavigatorIntakeForm.tsx

import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type {
  GeneratedIntakeSummary,
  IntakeFormState,
  NavigatorCaseWorkflow,
} from "../types";
import {
  INTAKE_BASICS_STEP_INDEX,
  INTAKE_CONSENT_FIELD_ID,
} from "../intakeConsent";
import {
  familyDoctorSituationOptions,
  multipleBirthOptions,
  priorityOverrideOptions,
  priorityOverrideReasonOptions,
  requestForOptions,
  yesNoNotSureOptions,
  yesNoPreferNotOptions,
} from "../intakeOptions";
import GeneratedSummaryPanel from "./GeneratedSummaryPanel";
import QuestionSelect from "./QuestionSelect";
import "./NavigatorIntakeForm.css";

type CoverageTypeItem = {
  CoverageTypeID: number;
  Name: string;
};

type LanguageItem = {
  LanguageID: number;
  Name: string;
};

export type IntakeStepNavigationRequest = {
  stepIndex: number;
  focusConsent: boolean;
  requestId: number;
};

type NavigatorIntakeFormProps = {
  workflow: NavigatorCaseWorkflow | null;
  intakeForm: IntakeFormState;
  generatedSummary: GeneratedIntakeSummary;
  formDisabled: boolean;
  isSavingIntake: boolean;
  intakeSaveError: string | null;
  intakeSaveSuccess: string | null;
  progressStatus: "saved" | "unsaved" | "saving" | "failed";
  progressStatusLabel: string;
  progressSaveError: string | null;
  canSaveProgress: boolean;
  isSavingProgress: boolean;
  priorityReasons: string[];
  intakeAnswers: string[];
  accessibilityNeeds: string[];
  stepNavigationRequest?: IntakeStepNavigationRequest | null;
  showPendingDraftReminder?: boolean;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void | Promise<void>;
  onSaveProgress: () => void;
  onCompleteIntakeFromReminder?: () => void;
  onChange: <K extends keyof IntakeFormState>(
    key: K,
    value: IntakeFormState[K]
  ) => void;
};

const preferredDoctorGenderOptions = [
  { value: "", label: "Not selected" },
  { value: "NoPreference", label: "No preference" },
  { value: "Female", label: "Female doctor preferred" },
  { value: "Male", label: "Male doctor preferred" },
];

const intakeSteps = [
  { label: "Basics", title: "Request basics" },
  { label: "Preferences", title: "Matching preferences" },
  { label: "HCC", title: "Current attachment / HCC information" },
  { label: "Circumstances", title: "Special circumstances" },
  { label: "Support", title: "Support needs" },
  { label: "Access", title: "Access & communication" },
  { label: "Review", title: "Review" },
] as const;

export default function NavigatorIntakeForm({
  workflow,
  intakeForm,
  generatedSummary,
  formDisabled,
  isSavingIntake,
  intakeSaveError,
  intakeSaveSuccess,
  progressStatus,
  progressStatusLabel,
  progressSaveError,
  canSaveProgress,
  isSavingProgress,
  stepNavigationRequest = null,
  showPendingDraftReminder = false,
  onSubmit,
  onSaveProgress,
  onCompleteIntakeFromReminder,
  onChange,
}: NavigatorIntakeFormProps) {
  const [coverageTypes, setCoverageTypes] = useState<CoverageTypeItem[]>([]);
  const [coverageTypesLoading, setCoverageTypesLoading] = useState(false);
  const [coverageTypesError, setCoverageTypesError] = useState<string | null>(
    null
  );

  const [languages, setLanguages] = useState<LanguageItem[]>([]);
  const [languagesLoading, setLanguagesLoading] = useState(false);
  const [languagesError, setLanguagesError] = useState<string | null>(null);
  const [activeIntakeStep, setActiveIntakeStep] = useState(0);
  const consentSelectRef = useRef<HTMLSelectElement | null>(null);
  const lastHandledNavigationRequestId = useRef<number | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadCoverageTypes() {
      setCoverageTypesLoading(true);
      setCoverageTypesError(null);

      try {
        const response = await fetch("/api/coverage-types", {
          method: "GET",
          credentials: "include",
        });

        const payload = (await response.json()) as
          | CoverageTypeItem[]
          | {
              error?: string;
            };

        if (!response.ok || !Array.isArray(payload)) {
          throw new Error(
            !Array.isArray(payload) && payload?.error
              ? payload.error
              : "Unable to load coverage types."
          );
        }

        if (!isMounted) return;

        setCoverageTypes(
          payload.filter(
            (item) =>
              Number.isFinite(Number(item?.CoverageTypeID)) &&
              Number(item.CoverageTypeID) > 0 &&
              typeof item?.Name === "string" &&
              item.Name.trim().length > 0
          )
        );
      } catch (err) {
        if (!isMounted) return;
        setCoverageTypes([]);
        setCoverageTypesError(
          err instanceof Error ? err.message : "Unable to load coverage types."
        );
      } finally {
        if (isMounted) setCoverageTypesLoading(false);
      }
    }

    loadCoverageTypes();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadLanguages() {
      setLanguagesLoading(true);
      setLanguagesError(null);

      try {
        const response = await fetch("/api/languages", {
          method: "GET",
          credentials: "include",
        });

        const payload = (await response.json()) as
          | LanguageItem[]
          | {
              error?: string;
            };

        if (!response.ok || !Array.isArray(payload)) {
          throw new Error(
            !Array.isArray(payload) && payload?.error
              ? payload.error
              : "Unable to load languages."
          );
        }

        if (!isMounted) return;

        setLanguages(
          payload.filter(
            (item) =>
              Number.isFinite(Number(item?.LanguageID)) &&
              Number(item.LanguageID) > 0 &&
              typeof item?.Name === "string" &&
              item.Name.trim().length > 0
          )
        );
      } catch (err) {
        if (!isMounted) return;
        setLanguages([]);
        setLanguagesError(
          err instanceof Error ? err.message : "Unable to load languages."
        );
      } finally {
        if (isMounted) setLanguagesLoading(false);
      }
    }

    loadLanguages();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!stepNavigationRequest) return;
    if (
      lastHandledNavigationRequestId.current === stepNavigationRequest.requestId
    ) {
      return;
    }

    lastHandledNavigationRequestId.current = stepNavigationRequest.requestId;
    const nextStep = Math.max(
      0,
      Math.min(
        intakeSteps.length - 1,
        Number(stepNavigationRequest.stepIndex) || INTAKE_BASICS_STEP_INDEX
      )
    );
    setActiveIntakeStep(nextStep);

    if (!stepNavigationRequest.focusConsent) return;

    const focusConsentField = () => {
      const node = consentSelectRef.current;
      if (!node) return false;
      node.focus();
      node.scrollIntoView({ block: "center", behavior: "smooth" });
      return true;
    };

    if (focusConsentField()) return;

    const frame = window.requestAnimationFrame(() => {
      if (focusConsentField()) return;
      window.setTimeout(() => {
        focusConsentField();
      }, 50);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [stepNavigationRequest]);

  const coverageTypeOptions = useMemo(
    () => [
      { value: "", label: "Not selected" },
      ...coverageTypes.map((item) => ({
        value: String(item.CoverageTypeID),
        label: item.Name,
      })),
    ],
    [coverageTypes]
  );

  const languageOptions = useMemo(
    () => [
      { value: "", label: "No preferred language selected" },
      ...languages.map((item) => ({
        value: String(item.LanguageID),
        label: item.Name,
      })),
    ],
    [languages]
  );

  const showOtherReason =
    intakeForm.familyDoctorSituation === "OtherNonMedical";
  const showHccFollowUp = intakeForm.hccRegistered === "yes";
  const showHccReferenceFields =
    showHccFollowUp && intakeForm.hccReferenceInfo === "yes";
  const showHospitalDischargeFollowUp =
    intakeForm.hospitalOvernightLast3Months === "yes";
  const showPregnancyBranch = intakeForm.currentlyPregnant === "yes";
  const showNewbornBranch = intakeForm.currentlyPregnant === "no";
  const showNewbornFollowUp =
    showNewbornBranch && intakeForm.babyLast2Weeks === "yes";
  const showFitFollowUp = intakeForm.fitPositiveOrCrcFollowUp === "yes";
  const showPriorityOverrideReason = Boolean(intakeForm.priorityOverrideLevel);

  function handleHccRegisteredChange(value: string) {
    onChange("hccRegistered", value);

    if (value !== "yes") {
      onChange("hccReferred", "");
      onChange("hccComplexVulnerable", "");
      onChange("hccReferenceInfo", "");
      onChange("hccReferenceNote", "");
      onChange("hccReferralDate", "");
    }
  }

  function handleHccReferenceInfoChange(value: string) {
    onChange("hccReferenceInfo", value);

    if (value !== "yes") {
      onChange("hccReferenceNote", "");
      onChange("hccReferralDate", "");
    }
  }

  function handleFitFollowUpChange(value: string) {
    onChange("fitPositiveOrCrcFollowUp", value);

    if (value !== "yes") {
      onChange("hasDoctorArrangingCrcFollowUp", "");
    }
  }

  function goToIntakeStep(step: number) {
    if (step < 0 || step >= intakeSteps.length) return;
    setActiveIntakeStep(step);
  }

  return (
    <section
      className="cn-case-detail-card cn-case-detail-wide"
      id="navigator-intake-form"
    >
      <div className="cn-case-detail-card-header">
        <div>
          <h2>Navigator intake workspace</h2>
          <p>
            Complete the standard request package and guided patient interview.
            The system calculates priority, HCC context, special population
            flags, and matching signals.
          </p>
        </div>
        <span className="cn-intake-edit-state">
          {workflow?.canEditIntake ? "Editable" : "Locked by workflow"}
        </span>
      </div>

      {showPendingDraftReminder ? (
        <div
          className="cn-intake-pending-draft-reminder"
          role="status"
        >
          <div>
            <strong>Updated Intake saved as draft</strong>
            <p>
              Complete Intake to apply these changes to matching and forwarding.
            </p>
          </div>
          {typeof onCompleteIntakeFromReminder === "function" ? (
            <button
              type="button"
              className="cn-intake-pending-draft-complete"
              onClick={onCompleteIntakeFromReminder}
              disabled={formDisabled || isSavingProgress || isSavingIntake}
            >
              {isSavingIntake ? "Completing intake..." : "Complete Intake"}
            </button>
          ) : null}
        </div>
      ) : null}

      <form className="cn-case-detail-intake-form" onSubmit={onSubmit}>
        <nav className="cn-intake-steps" aria-label="Intake steps">
          {intakeSteps.map((step, index) => (
            <button
              key={step.label}
              type="button"
              className={
                index === activeIntakeStep
                  ? "cn-intake-step is-active"
                  : "cn-intake-step"
              }
              aria-current={index === activeIntakeStep ? "step" : undefined}
              onClick={() => goToIntakeStep(index)}
            >
              {step.label}
            </button>
          ))}
        </nav>

        <h3 className="cn-intake-step-title">
          {intakeSteps[activeIntakeStep].title}
        </h3>

        {activeIntakeStep === 0 ? (
          <>
        <div className="cn-case-detail-form-header">
          <div>
            <h3>Standard family doctor request information</h3>
            <p>
              These fields complete the normal family doctor request package
              before this navigator case can be forwarded.
            </p>
          </div>
        </div>

        <div className="cn-case-detail-form-grid">
          <QuestionSelect
            label="Who is this request for?"
            value={intakeForm.requestFor}
            options={requestForOptions}
            disabled={formDisabled}
            onChange={(value) => onChange("requestFor", value)}
          />

          <QuestionSelect
            label="Which of the following best describes the patient's current family doctor situation?"
            value={intakeForm.familyDoctorSituation}
            options={familyDoctorSituationOptions}
            disabled={formDisabled}
            onChange={(value) => onChange("familyDoctorSituation", value)}
          />

          <QuestionSelect
            label="Coverage type"
            value={intakeForm.coverageType}
            options={coverageTypeOptions}
            disabled={formDisabled || coverageTypesLoading}
            helperText={
              coverageTypesError
                ? `Could not load coverage types: ${coverageTypesError}`
                : "Select the same coverage type used in the public patient request form."
            }
            onChange={(value) => onChange("coverageType", value)}
          />

          <QuestionSelect
            label="Has the patient/applicant agreed to submit this request through AcceptingMD?"
            value={intakeForm.consentAccepted}
            options={yesNoNotSureOptions}
            disabled={formDisabled}
            helperText="Submitting a request does not guarantee acceptance."
            selectId={INTAKE_CONSENT_FIELD_ID}
            selectRef={consentSelectRef}
            onChange={(value) => onChange("consentAccepted", value)}
          />
        </div>

        {showOtherReason ? (
          <label className="cn-case-detail-form-full">
            <span>Please briefly explain the non-medical reason</span>
            <textarea
              value={intakeForm.otherReasonText}
              onChange={(event) =>
                onChange("otherReasonText", event.target.value)
              }
              disabled={formDisabled}
              rows={3}
              placeholder="Please do not enter medical history."
            />
          </label>
        ) : null}
          </>
        ) : null}

        {activeIntakeStep === 1 ? (
        <section className="cn-case-detail-intake-panel">
          <h3>Matching preferences</h3>
          <p className="cn-case-detail-muted">
            These preferences help the navigator matching engine prioritize
            suitable providers. They do not guarantee the selected doctor or
            clinic can accommodate the preference.
          </p>

          <div className="cn-case-detail-form-grid">
            <QuestionSelect
              label="Preferred doctor gender"
              value={intakeForm.preferredDoctorGender}
              options={preferredDoctorGenderOptions}
              disabled={formDisabled}
              helperText="Use No preference when gender does not matter for matching."
              onChange={(value) => onChange("preferredDoctorGender", value)}
            />

            <QuestionSelect
              label="Preferred doctor language"
              value={intakeForm.preferredLanguageId}
              options={languageOptions}
              disabled={formDisabled || languagesLoading}
              helperText={
                languagesError
                  ? `Could not load languages: ${languagesError}`
                  : "Select the patient's preferred provider language if relevant."
              }
              onChange={(value) => onChange("preferredLanguageId", value)}
            />
          </div>
        </section>
        ) : null}

        {activeIntakeStep === 2 ? (
        <section className="cn-case-detail-intake-panel">
          <h3>Health Care Connect</h3>
          <div className="cn-case-detail-form-grid">
            <QuestionSelect
              label="Are you currently registered with Health Care Connect?"
              value={intakeForm.hccRegistered}
              options={yesNoNotSureOptions}
              disabled={formDisabled}
              onChange={handleHccRegisteredChange}
            />

            {showHccFollowUp ? (
              <>
                <QuestionSelect
                  label="Were you referred from Health Care Connect for help finding a family doctor?"
                  value={intakeForm.hccReferred}
                  options={yesNoNotSureOptions}
                  disabled={formDisabled}
                  onChange={(value) => onChange("hccReferred", value)}
                />

                <QuestionSelect
                  label="Has Health Care Connect told you that you are complex or vulnerable?"
                  value={intakeForm.hccComplexVulnerable}
                  options={yesNoNotSureOptions}
                  disabled={formDisabled}
                  onChange={(value) => onChange("hccComplexVulnerable", value)}
                />

                <QuestionSelect
                  label="Do you have any Health Care Connect reference information?"
                  value={intakeForm.hccReferenceInfo}
                  options={yesNoNotSureOptions}
                  disabled={formDisabled}
                  onChange={handleHccReferenceInfoChange}
                />
              </>
            ) : null}
          </div>

          {showHccReferenceFields ? (
            <div className="cn-case-detail-form-grid">
              <label>
                <span>HCC reference / note</span>
                <input
                  value={intakeForm.hccReferenceNote}
                  onChange={(event) =>
                    onChange("hccReferenceNote", event.target.value)
                  }
                  disabled={formDisabled}
                  placeholder="Optional"
                />
              </label>

              <label>
                <span>HCC referral or contact date</span>
                <input
                  type="date"
                  value={intakeForm.hccReferralDate}
                  onChange={(event) =>
                    onChange("hccReferralDate", event.target.value)
                  }
                  disabled={formDisabled}
                />
              </label>
            </div>
          ) : null}
        </section>
        ) : null}

        {activeIntakeStep === 3 ? (
          <>
        <section className="cn-case-detail-intake-panel">
          <h3>Recent hospital discharge</h3>
          <div className="cn-case-detail-form-grid">
            <QuestionSelect
              label="Have you been admitted to hospital overnight in the last 3 months?"
              value={intakeForm.hospitalOvernightLast3Months}
              options={yesNoNotSureOptions}
              disabled={formDisabled}
              onChange={(value) =>
                onChange("hospitalOvernightLast3Months", value)
              }
            />

            {showHospitalDischargeFollowUp ? (
              <>
                <QuestionSelect
                  label="Were you discharged without having a family doctor?"
                  value={intakeForm.dischargedWithoutFamilyDoctor}
                  options={yesNoNotSureOptions}
                  disabled={formDisabled}
                  onChange={(value) =>
                    onChange("dischargedWithoutFamilyDoctor", value)
                  }
                />

                <label>
                  <span>What was your discharge date?</span>
                  <input
                    type="date"
                    value={intakeForm.dischargeDate}
                    onChange={(event) =>
                      onChange("dischargeDate", event.target.value)
                    }
                    disabled={formDisabled || intakeForm.dischargeDateUnknown}
                  />
                </label>

                <label className="cn-case-detail-check">
                  <input
                    type="checkbox"
                    checked={intakeForm.dischargeDateUnknown}
                    onChange={(event) =>
                      onChange("dischargeDateUnknown", event.target.checked)
                    }
                    disabled={formDisabled}
                  />
                  <span>Discharge date unknown</span>
                </label>
              </>
            ) : null}
          </div>
        </section>

        <section className="cn-case-detail-intake-panel">
          <h3>Pregnancy, newborn, mother/newborn</h3>
          <div className="cn-case-detail-form-grid">
            <QuestionSelect
              label="Are you currently pregnant?"
              value={intakeForm.currentlyPregnant}
              options={yesNoPreferNotOptions}
              disabled={formDisabled}
              onChange={(value) => onChange("currentlyPregnant", value)}
            />

            {showPregnancyBranch ? (
              <>
                <QuestionSelect
                  label="Are you 30 weeks pregnant or more?"
                  value={intakeForm.pregnancy30WeeksOrMore}
                  options={yesNoNotSureOptions}
                  disabled={formDisabled}
                  onChange={(value) =>
                    onChange("pregnancy30WeeksOrMore", value)
                  }
                />

                <QuestionSelect
                  label="Do you currently have a family doctor?"
                  value={intakeForm.pregnantHasFamilyDoctor}
                  options={yesNoNotSureOptions}
                  disabled={formDisabled}
                  onChange={(value) =>
                    onChange("pregnantHasFamilyDoctor", value)
                  }
                />
              </>
            ) : null}

            {showNewbornBranch ? (
              <QuestionSelect
                label="Have you had a baby in the last 2 weeks?"
                value={intakeForm.babyLast2Weeks}
                options={yesNoNotSureOptions}
                disabled={formDisabled}
                onChange={(value) => onChange("babyLast2Weeks", value)}
              />
            ) : null}

            {showNewbornFollowUp ? (
              <>
                <QuestionSelect
                  label="Does the newborn currently have a family doctor?"
                  value={intakeForm.newbornHasFamilyDoctor}
                  options={yesNoNotSureOptions}
                  disabled={formDisabled}
                  onChange={(value) =>
                    onChange("newbornHasFamilyDoctor", value)
                  }
                />

                <QuestionSelect
                  label="Was this a multiple birth?"
                  value={intakeForm.multipleBirth}
                  options={multipleBirthOptions}
                  disabled={formDisabled}
                  onChange={(value) => onChange("multipleBirth", value)}
                />

                <QuestionSelect
                  label="Was the newborn admitted to NICU in the last 3 months?"
                  value={intakeForm.newbornNicuLast3Months}
                  options={yesNoNotSureOptions}
                  disabled={formDisabled}
                  onChange={(value) =>
                    onChange("newbornNicuLast3Months", value)
                  }
                />
              </>
            ) : null}
          </div>
        </section>

        <section className="cn-case-detail-intake-panel">
          <h3>FIT / colorectal screening follow-up</h3>
          <div className="cn-case-detail-form-grid">
            <QuestionSelect
              label="Have you recently been told you had a positive FIT test or need follow-up for colorectal cancer screening?"
              value={intakeForm.fitPositiveOrCrcFollowUp}
              options={yesNoPreferNotOptions}
              disabled={formDisabled}
              onChange={handleFitFollowUpChange}
            />

            {showFitFollowUp ? (
              <QuestionSelect
                label="Do you currently have a family doctor arranging this follow-up?"
                value={intakeForm.hasDoctorArrangingCrcFollowUp}
                options={yesNoNotSureOptions}
                disabled={formDisabled}
                onChange={(value) =>
                  onChange("hasDoctorArrangingCrcFollowUp", value)
                }
              />
            ) : null}
          </div>
        </section>
          </>
        ) : null}

        {activeIntakeStep === 4 ? (
        <section className="cn-case-detail-intake-panel">
          <h3>Support needs</h3>
          <div className="cn-case-detail-form-grid">
            <QuestionSelect
              label="Do you take 3 or more regular prescription medications?"
              value={intakeForm.regularMedications3Plus}
              options={yesNoNotSureOptions}
              disabled={formDisabled}
              onChange={(value) => onChange("regularMedications3Plus", value)}
            />

            <QuestionSelect
              label="Do you have one or more ongoing medical conditions that require regular follow-up?"
              value={intakeForm.ongoingConditionsRegularFollowUp}
              options={yesNoNotSureOptions}
              disabled={formDisabled}
              onChange={(value) =>
                onChange("ongoingConditionsRegularFollowUp", value)
              }
            />

            <QuestionSelect
              label="Have you had trouble managing appointments, transportation, forms, or follow-up care?"
              value={intakeForm.troubleManagingCare}
              options={yesNoNotSureOptions}
              disabled={formDisabled}
              onChange={(value) => onChange("troubleManagingCare", value)}
            />

            <QuestionSelect
              label="Do you need support for mental health, addiction, or significant stress affecting your care?"
              value={intakeForm.mentalHealthAddictionStressSupport}
              options={yesNoPreferNotOptions}
              disabled={formDisabled}
              onChange={(value) =>
                onChange("mentalHealthAddictionStressSupport", value)
              }
            />

            <QuestionSelect
              label="Do you need mobility, disability, or accessibility support when attending appointments?"
              value={intakeForm.mobilityDisabilityAccessibilitySupport}
              options={yesNoNotSureOptions}
              disabled={formDisabled}
              onChange={(value) =>
                onChange("mobilityDisabilityAccessibilitySupport", value)
              }
            />

            <QuestionSelect
              label="Do you need help communicating in English during medical visits?"
              value={intakeForm.helpCommunicatingEnglish}
              options={yesNoNotSureOptions}
              disabled={formDisabled}
              helperText="If yes, complete Preferred doctor language in the Matching preferences section."
              onChange={(value) => onChange("helpCommunicatingEnglish", value)}
            />
          </div>
        </section>
        ) : null}

        {activeIntakeStep === 5 ? (
        <section className="cn-case-detail-intake-panel">
          <h3>Practical access and communication</h3>
          <div className="cn-case-detail-form-grid">
            <QuestionSelect
              label="Is phone the best way to reach you?"
              value={intakeForm.phoneBestContact}
              options={yesNoNotSureOptions}
              disabled={formDisabled}
              onChange={(value) => onChange("phoneBestContact", value)}
            />

            <QuestionSelect
              label="Do you have reliable access to email?"
              value={intakeForm.reliableEmailAccess}
              options={yesNoNotSureOptions}
              disabled={formDisabled}
              onChange={(value) => onChange("reliableEmailAccess", value)}
            />

            <QuestionSelect
              label="Do you need help booking or attending the first appointment?"
              value={intakeForm.helpBookingOrAttending}
              options={yesNoNotSureOptions}
              disabled={formDisabled}
              onChange={(value) => onChange("helpBookingOrAttending", value)}
            />
          </div>
        </section>
        ) : null}

        {activeIntakeStep === 6 ? (
          <>
        <section className="cn-case-detail-intake-panel">
          <h3>System-generated summary</h3>
          <p className="cn-case-detail-muted">
            Preview only — calculated in the browser from the current interview
            answers and date of birth. The server recalculates and stores the
            authoritative values when you save intake.
          </p>
          <GeneratedSummaryPanel summary={generatedSummary} />
        </section>

        <section className="cn-case-detail-intake-panel">
          <h3>Priority override</h3>
          <p className="cn-case-detail-muted">
            Use only when the system-calculated priority does not reflect the
            navigator conversation. A reason is required.
          </p>

          <div className="cn-case-detail-form-grid">
            <QuestionSelect
              label="Priority override"
              value={intakeForm.priorityOverrideLevel}
              options={priorityOverrideOptions}
              disabled={formDisabled}
              onChange={(value) => onChange("priorityOverrideLevel", value)}
            />

            {showPriorityOverrideReason ? (
              <QuestionSelect
                label="Override reason"
                value={intakeForm.priorityOverrideReason}
                options={priorityOverrideReasonOptions}
                disabled={formDisabled}
                onChange={(value) => onChange("priorityOverrideReason", value)}
              />
            ) : null}
          </div>

          {showPriorityOverrideReason ? (
            <label className="cn-case-detail-form-full">
              <span>Priority override note</span>
              <textarea
                value={intakeForm.priorityOverrideNote}
                onChange={(event) =>
                  onChange("priorityOverrideNote", event.target.value)
                }
                disabled={formDisabled}
                rows={3}
                placeholder="Optional unless override reason is Other."
              />
            </label>
          ) : null}
        </section>

        <label className="cn-case-detail-form-full">
          <span>Navigator summary for clinic</span>
          <textarea
            value={intakeForm.navigatorSummary}
            onChange={(event) =>
              onChange("navigatorSummary", event.target.value)
            }
            disabled={formDisabled}
            rows={4}
            placeholder="Briefly summarize practical context that may help the clinic understand the request. Do not enter detailed medical history."
          />
        </label>

        <label className="cn-case-detail-form-full">
          <span>Save note</span>
          <textarea
            value={intakeForm.note}
            onChange={(event) => onChange("note", event.target.value)}
            disabled={formDisabled}
            rows={3}
            placeholder="Optional internal note for the activity timeline."
          />
          <p className="cn-intake-note-hint">
            This note is saved with Save Progress. Completing intake also records
            it on the activity timeline.
          </p>
        </label>

        {intakeSaveError ? (
          <div className="cn-case-detail-form-error" role="alert">
            {intakeSaveError}
          </div>
        ) : null}

        {intakeSaveSuccess ? (
          <div className="cn-case-detail-form-success" role="status">
            {intakeSaveSuccess}
          </div>
        ) : null}

        <div className="cn-case-detail-form-actions">
          <button
            type="submit"
            className="cn-intake-complete-button"
            disabled={formDisabled || isSavingProgress}
          >
            {isSavingIntake ? "Completing intake..." : "Complete intake"}
          </button>
        </div>
          </>
        ) : null}

        <div className="cn-intake-step-nav">
          <button
            type="button"
            className="cn-intake-nav-back"
            onClick={() => goToIntakeStep(activeIntakeStep - 1)}
            disabled={activeIntakeStep === 0}
          >
            Back
          </button>
          <div className="cn-intake-step-nav-end">
            <div className="cn-intake-progress">
              <p
                className={`cn-intake-progress-status is-${progressStatus}`}
                role="status"
                aria-live="polite"
              >
                {progressStatusLabel}
              </p>
              {progressSaveError ? (
                <div className="cn-case-detail-form-error" role="alert">
                  {progressSaveError}
                </div>
              ) : null}
              <button
                type="button"
                className="cn-intake-nav-save"
                onClick={onSaveProgress}
                disabled={!canSaveProgress}
              >
                Save Progress
              </button>
            </div>
            <button
              type="button"
              className="cn-intake-nav-next"
              onClick={() => goToIntakeStep(activeIntakeStep + 1)}
              disabled={activeIntakeStep === intakeSteps.length - 1}
            >
              Next
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}